import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import PdpSharedCustomsMessage from './PdpSharedCustomsMessage';

//components
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import {
    anywhereInWorld,
    ratesVary,
    freeQuote,
    quoteMethod,
    quotePending,
    quotePrice,
    quoteRegion,
    quoteHandlingTime,
    leadTimeMessage,
    shippingMethodDescriptions,
} from '../messages/pdpShippingInfoMessages';

import { getIsFreeShipping } from 'dibs-search-product-tile/exports/freeShippingHelpers';
import { getShippingPrice, REGION_CODES } from '../helpers/shippingHelpers';
import { useUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';

import styles from './PdpSharedShippingInfo.scss';

export const PdpSharedShippingInfoComponent = ({
    regionalInfo,
    item,
    user,
    currency,
    children,
    shipmentQuote,
    shipment,
    quoteRequestStatus,
    leadTime,
    renderTooltip,
    noLineBreaks,
    showCustomsInfo,
    isClient,
}) => {
    const [tooltipHover, setTooltipHover] = useState(false);
    const userSessionRegions = useUserSessionRegions();

    const relevantQuote = shipmentQuote || null;

    const {
        buyerTotalAmount,
        buyerServiceMethodDisplay: shippingMethodDisplay,
        serviceMethod,
        handlingTimeDisplay,
        showQuoteAsPending,
    } = relevantQuote || {};

    const convertedAmountList = buyerTotalAmount?.convertedAmountList;
    const regionCode = serviceMethod?.region?.code;
    const customQuoteShippingAddress = shipment?.shippingAddress?.displayCityStateCountry;
    let regionName =
        quoteRequestStatus === 'customQuoteProvided' ||
        quoteRequestStatus === 'customQuoteRequested'
            ? customQuoteShippingAddress
            : serviceMethod?.region?.regionName;

    const price = getShippingPrice({ convertedAmountList, currency, shortMessage: true });
    const userRegions = userSessionRegions || regionalInfo?.[0]?.regionsByZipCode || [];
    const isFreeShipping = getIsFreeShipping({ item, userRegions });
    const shippingPriceQuote = isFreeShipping ? freeQuote : quotePrice(price);
    const shippingMethodQuote = quoteMethod(shippingMethodDisplay);

    let overviewMessage;

    if (regionCode === REGION_CODES.REST_OF_WORLD) {
        regionName = anywhereInWorld;
    }
    if (!relevantQuote || quoteRequestStatus === 'requestQuote') {
        overviewMessage = ratesVary;
    } else if (quoteRequestStatus === 'customQuoteRequested' || showQuoteAsPending) {
        overviewMessage = (
            <span data-tn="quote-requested">{quotePending(customQuoteShippingAddress)}</span>
        );
    } else {
        overviewMessage = (
            <span data-tn="prequote-overview">
                {shippingPriceQuote}
                <span
                    className={styles.hoverable}
                    onMouseEnter={() => {
                        setTooltipHover(true);
                    }}
                    onMouseLeave={() => {
                        setTooltipHover(false);
                    }}
                >
                    {shippingMethodQuote}
                    {renderTooltip && (
                        <Tooltip isVisible={tooltipHover} direction="bottom">
                            {shippingMethodDescriptions[shippingMethodDisplay]}
                        </Tooltip>
                    )}
                </span>{' '}
                {quoteRegion(regionName)}
                {!noLineBreaks && <div />}
                {quoteHandlingTime(handlingTimeDisplay)}
                {leadTime !== 'available-now' && <span data-tn="lead-time">{leadTimeMessage}</span>}
                {'.'}
            </span>
        );
    }
    return (
        <span className={styles.shippingInfoContainer}>
            <PdpSharedPromoCode
                onlyShippingPromos
                itemId={item?.serviceId}
                user={user}
                userId={user?.serviceId}
            />
            {overviewMessage} {children}
            {showCustomsInfo && <PdpSharedCustomsMessage item={item} isClient={isClient} />}
        </span>
    );
};

PdpSharedShippingInfoComponent.propTypes = {
    regionalInfo: PropTypes.array,
    item: PropTypes.object,
    shipmentQuote: PropTypes.object,
    shipment: PropTypes.object,
    currency: PropTypes.string.isRequired,
    quoteRequestStatus: PropTypes.string,
    children: PropTypes.node,
    leadTime: PropTypes.string,
    renderTooltip: PropTypes.bool,
    noLineBreaks: PropTypes.bool,
    user: PropTypes.object,
    showCustomsInfo: PropTypes.bool,
    isClient: PropTypes.bool,
};

export const PdpSharedShippingInfo = createFragmentContainer(PdpSharedShippingInfoComponent, {
    regionalInfo: graphql`
        fragment PdpSharedShippingInfo_regionalInfo on RegionalInfo @relay(plural: true) {
            regionsByZipCode {
                displayName
            }
        }
    `,
    item: graphql`
        fragment PdpSharedShippingInfo_item on Item {
            serviceId
            ...freeShippingHelpers_item
            ...PdpSharedCustomsMessage_item
        }
    `,
    shipment: graphql`
        fragment PdpSharedShippingInfo_shipment on Shipment {
            shippingAddress {
                displayCityStateCountry
            }
        }
    `,
    shipmentQuote: graphql`
        fragment PdpSharedShippingInfo_shipmentQuote on ShipmentQuote {
            handlingTimeDisplay(displayShort: true)
            totalAmount {
                currency
                amount
                convertedAmountList {
                    currency
                    amount
                }
            }
            buyerTotalAmount {
                convertedAmountList {
                    currency
                    amount
                }
            }
            buyerServiceMethodDisplay
            serviceMethod {
                serviceMethodType
                region {
                    apiType
                    code
                    regionName
                    regionType
                }
            }
            showQuoteAsPending
        }
    `,
    user: graphql`
        fragment PdpSharedShippingInfo_user on User {
            serviceId
            ...PdpSharedPromoCode_user
        }
    `,
});
